import React from 'react';

import { ScreenProps } from '../ScreenBase';
import styles from './input.module.css';
import { Input as AntInput, Form, Select, InputRef } from 'antd';
import NextButton from '../NextButton';
import { Rule } from 'antd/lib/form';
import { FieldData } from 'rc-field-form/lib/interface';
import ReactInputMask from 'react-input-mask';

interface InputState {
  text: string;
  isValid: boolean;
  value?: string;
  more?: any;
}

class Input extends React.Component<ScreenProps, InputState> {
  inputRef: React.RefObject<InputRef>;

  state: InputState = {
    text: '',
    isValid: false,
  };

  constructor(props: ScreenProps) {
    super(props);

    this.inputRef = React.createRef();
  }

  componentDidMount() {
    if (!this.props.data.required) this.setState({ isValid: true });
  }

  componentWillUnmount() {}

  onSubmit = () => {
    const { onSubmit } = this.props;
    const { text, value } = this.state;

    onSubmit({ text: text || undefined, meta: value });
  };

  getValidationRules = (): Rule[] => {
    const {
      data: { required, max_value, min_value, subtype, extended_props },
    } = this.props;

    const { validation_message } = extended_props || {};

    const validationRules: Rule[] = [];

    if (required && subtype !== 'date_no_year') {
      validationRules.push({
        required,
        message:
          validation_message ?? 'Debes diligenciar este campo para continuar',
      } as Rule);
    }

    if (subtype === 'email') {
      //Email
      validationRules.push({
        type: 'email',
        message: validation_message ?? 'Ingresa un correo electrónico válido',
      });
    }

    if (subtype === 'text' || !subtype) {
      //Text input
      if (max_value && min_value) {
        validationRules.push({
          type: 'string',
          max: max_value,
          min: min_value,
          message: validation_message
            ? validation_message
                .replace('{min_value}', min_value)
                .replace('{max_value}', max_value)
            : max_value !== min_value
            ? `El campo debe ser de más de ${min_value} y menos de ${max_value} caracteres.`
            : `El campo debe ser de ${max_value} caracteres.`,
        });
      } else if (max_value) {
        validationRules.push({
          type: 'string',
          max: max_value,
          message: validation_message
            ? validation_message.replace('{max_value}', max_value)
            : `El campo debe ser de menos de ${max_value} caracteres.`,
        });
      } else if (min_value) {
        validationRules.push({
          type: 'string',
          min: min_value,
          message: validation_message
            ? validation_message.replace('{min_value}', min_value)
            : `El campo debe ser de mas de ${min_value} caracteres.`,
        });
      }
    }

    if (subtype === 'mobile') {
      validationRules.push({
        pattern: new RegExp(/\d{3} \d{3} \d{2} \d{2}/g),
        message:
          validation_message ??
          'El valor debe ser un número de celular (ej. 300 123 45 67)',
      });
    }

    if (subtype === 'digits_only') {
      validationRules.push({
        pattern: new RegExp(`\\d{${max_value}}`, 'g'),
        message: validation_message
          ? validation_message.replace('{max_value}', max_value)
          : `El valor debe ser numérico de ${max_value} caracteres.`,
      });
    }

    if (subtype === 'number') {
      //Numeric
      if (max_value && min_value) {
        validationRules.push({
          type: 'number',
          max: max_value,
          min: min_value,
          message: validation_message
            ? validation_message
                .replace('{max_value}', max_value)
                .replace('{min_value}', min_value)
            : `El valor debe estar entre ${min_value} y ${max_value}`,
        });
      } else if (max_value) {
        validationRules.push({
          type: 'number',
          max: max_value,
          message: validation_message
            ? validation_message.replace('{max_value}', max_value)
            : `El valor debe ser menor a ${max_value}`,
        });
      } else if (min_value) {
        validationRules.push({
          type: 'number',
          min: min_value,
          message: validation_message
            ? validation_message.replace('{min_value}', min_value)
            : `El valor debe ser mayor a ${min_value}`,
        });
      }
    }

    return validationRules;
  };

  onChange = (fields: FieldData[]) => {
    if (!fields || fields.length === 0) return;
    this.setState({
      isValid: !fields[0].errors || fields[0].errors.length === 0,
    });
  };

  onMonthSelected = (value: string) => {
    const { more } = this.state;
    const { day } = more || {};
    const isValid = !!(day && value);
    this.setState({
      isValid,
      more: { ...this.state.more, month: value },
      text: `${value} ${this.state.more ? this.state.more.day : ''}`,
    });
  };

  onDaySelected = (value: string) => {
    const { more } = this.state;
    const { month } = more || {};
    const isValid = !!(value && month);
    this.setState({
      isValid,
      more: { ...this.state.more, day: value },
      text: `${this.state.more ? this.state.more.month : ''} ${value}`,
    });
  };

  renderControl = () => {
    const {
      data: { extended_props, subtype, max_value },
      locked,
      responses,
    } = this.props;
    const { more } = this.state;
    const { day, month } = more || {};

    const opts = (extended_props?.options || []).filter(
      (o: any, idx: number, all: any[]) => {
        const noDuplicate =
          all.findIndex((a) => a.text === o.text && a.subtext === o.subtext) ===
          idx;

        const safisfiesFilters =
          !extended_props?.tags_config?.filters ||
          extended_props.tags_config.filters
            .filter((f: any) => f.type === 'question')
            .every(
              (f: any) =>
                o[`filter_value_${f.question_id}`] ===
                (responses[f.question_id].text || responses[f.question_id].meta)
            );

        return noDuplicate && safisfiesFilters;
      }
    );

    switch (subtype) {
      case 'digits_only':
        const mask = '9'.repeat(max_value || 1);
        return (
          <ReactInputMask
            mask={mask}
            disabled={locked}
            alwaysShowMask={false}
            onChange={(e) => this.setState({ text: e.target.value })}
          >
            <AntInput
              type='text'
              ref={this.inputRef}
              placeholder={
                (extended_props || {}).input_placeholder || 'Escribe aquí ...'
              }
              size='large'
            />
          </ReactInputMask>
        );
      case 'mobile':
        return (
          <ReactInputMask
            mask='999 999 99 99'
            disabled={locked}
            alwaysShowMask={false}
            onChange={(e) => this.setState({ text: e.target.value })}
          >
            <AntInput
              type='tel'
              ref={this.inputRef}
              placeholder={
                (extended_props || {}).input_placeholder || 'Escribe aquí ...'
              }
              size='large'
            />
          </ReactInputMask>
        );
      case 'autocomplete':
        return (
          <Select
            style={{ width: '100%' }}
            disabled={locked}
            placeholder={
              (extended_props || {}).input_placeholder || 'Escribe aquí ...'
            }
            size='large'
            showSearch
            allowClear
            optionFilterProp='label'
            optionLabelProp='label'
            labelInValue
            onChange={(val) =>
              this.setState({ text: val?.label ?? '', value: val?.value ?? '' })
            }
          >
            {opts.map((opt: any) => (
              <Select.Option
                value={opt.value}
                label={opt.text}
                key={`${opt.text}_${opt.subtext}`}
              >
                <div className='demo-option-label-item'>
                  {opt.text}
                  <div className={styles.optSubtitle}>{opt.subtext}</div>
                </div>
              </Select.Option>
            ))}
          </Select>
        );
      case 'select':
        return (
          <Select
            style={{ width: '100%' }}
            disabled={locked}
            placeholder={
              (extended_props || {}).input_placeholder || 'Selecciona'
            }
            size='large'
            optionLabelProp='label'
            onChange={(value) =>
              this.setState({ text: value ? value.toString() : '' })
            }
          >
            {(extended_props.options || []).map((opt: any) => (
              <Select.Option value={opt.text} label={opt.text} key={opt.text}>
                <div className='demo-option-label-item'>
                  {opt.text}
                  <div className={styles.optSubtitle}>{opt.subtext}</div>
                </div>
              </Select.Option>
            ))}
          </Select>
        );
      case 'date_no_year':
        return (
          <div className={styles.dateNoYear}>
            <Select
              disabled={locked}
              placeholder='Día'
              size='large'
              value={day}
              onChange={this.onDaySelected}
            >
              {[...Array(31).keys()]
                .map((i) => `${i + 1}`)
                .map((opt: string) => (
                  <Select.Option value={opt} label={opt} key={opt}>
                    {opt}
                  </Select.Option>
                ))}
            </Select>
            <span> de </span>
            <Select
              disabled={locked}
              placeholder='Mes'
              size='large'
              value={month}
              onChange={this.onMonthSelected}
            >
              {[
                'Enero',
                'Febrero',
                'Marzo',
                'Abril',
                'Mayo',
                'Junio',
                'Julio',
                'Agosto',
                'Septiembre',
                'Octubre',
                'Noviembre',
                'Diciembre',
              ].map((opt: string) => (
                <Select.Option value={opt} label={opt} key={opt}>
                  {opt}
                </Select.Option>
              ))}
            </Select>
          </div>
        );
      case 'multiline':
        return (
          <AntInput.TextArea
            maxLength={max_value}
            disabled={locked}
            placeholder={
              (extended_props || {}).input_placeholder || 'Escribe aquí ...'
            }
            style={
              (extended_props || {}).uppercase
                ? { textTransform: 'uppercase' }
                : undefined
            }
            rows={(extended_props || {}).rows || 5}
            onChange={(e) => this.setState({ text: e.target.value })}
          />
        );

      default:
        const inputType =
          subtype === 'email'
            ? 'email'
            : subtype === 'date'
            ? 'date'
            : subtype === 'number'
            ? 'number'
            : 'text';
        return (
          <AntInput
            type={inputType}
            maxLength={
              inputType === 'text' && max_value ? max_value : undefined
            }
            ref={this.inputRef}
            disabled={locked}
            placeholder={
              (extended_props || {}).input_placeholder || 'Escribe aquí ...'
            }
            size='large'
            style={
              (extended_props || {}).uppercase
                ? { textTransform: 'uppercase' }
                : undefined
            }
            onChange={(e) => this.setState({ text: e.target.value })}
          />
        );
    }
  };

  render() {
    const {
      locked,
      data: { extended_props },
    } = this.props;
    const { isValid } = this.state;
    const { button_text } = extended_props || {};
    const validationRules = this.getValidationRules();

    return (
      <div className={styles.inputContainer}>
        <Form
          name='basic'
          onFinish={this.onSubmit}
          onFieldsChange={this.onChange}
        >
          <Form.Item name='input' rules={validationRules}>
            {this.renderControl()}
          </Form.Item>
          <Form.Item>
            <NextButton
              htmlType='submit'
              visible={isValid && !locked}
              text={button_text}
            />
          </Form.Item>
        </Form>
      </div>
    );
  }
}

export default Input;
